import { Component, input, output, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Device, getDeviceIcon } from '../../services/device.service';
import { AirQualityService } from '../../services/air-quality.service';
import { EQUIPMENT_POINT_TAG_MAPPING } from '../../../../core/mqtt/omni-mqtt-mapping.data';
import { AuthenticateService } from '../../../../core/services/authenticate.service';



@Component({
    selector: 'app-device-popup',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './device-popup.component.html',
    styleUrl: './device-popup.component.scss'
})
export class DevicePopupComponent {
    private airQualityService = inject(AirQualityService);
    private router = inject(Router);
    private sanitizer = inject(DomSanitizer);
    private authService = inject(AuthenticateService);

    device = input.required<Device>();
    close = output<void>();

    iconClass = computed(() => getDeviceIcon(this.device().type));

    displayName = computed(() => {
        const name = this.device().name;
        const targetNames = ['捲簾_14F-001', '捲簾_14F-004', '捲簾_15F-001', '捲簾_15F-003'];
        if (targetNames.includes(name)) {
            return `${name} (群組)`;
        }
        return name;
    });

    private lastWebrtcUrlStr: string | null = null;
    private cachedSafeUrl: SafeResourceUrl | null = null;

    webrtcUrl = computed<SafeResourceUrl | null>(() => {
        const configArr = this.device().config;
        if (!configArr || configArr.length === 0) return null;

        // 尋找包含 webrtc 設定的配置項目 (例如 CAM1_VIDEO)
        const videoConfig = configArr.find(c => c.range && c.range.webrtc);
        if (videoConfig && videoConfig.range.webrtc) {
            let currentUrlStr = videoConfig.range.webrtc;
            const token = this.authService.accessToken();
            if (token) {
                const separator = currentUrlStr.includes('?') ? '&' : '?';
                currentUrlStr = `${currentUrlStr}${separator}token=${token}`;
            }

            // 避免每次執行 computed 都產生新的 SafeResourceUrl 物件記憶體參考，導致 iframe 閃爍
            if (this.lastWebrtcUrlStr !== currentUrlStr) {
                this.lastWebrtcUrlStr = currentUrlStr;
                this.cachedSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(currentUrlStr);
            }
            return this.cachedSafeUrl;
        }
        return null;
    });

    comfortStatus = computed(() => {
        const val = this.device().data?.['V1_Comfort']?.value;
        return this.airQualityService.getComfortIndicator(val);
    });

    qualityStatus = computed(() => {
        const val = this.device().data?.['V1_AQI']?.value;
        return this.airQualityService.getQualityIndicator(val);
    });

    isThreePhaseMeter = computed(() => {
        const name = this.device().name;
        const threePhaseMeters = ['電錶_14F-002', '電錶_14F-003', '電錶_15F-002', '電錶_15F-003'];
        return threePhaseMeters.includes(name);
    });

    /**
     * 照明控盤點位清單
     * 以 config 為主要來源（保證有 name），搭配 data 取得即時值。
     * 排除技術性點位（Online / ErrStatus / Point 等開頭的內部用途點位）。
     */
    lightPoints = computed(() => {
        const EXCLUDED = new Set(['Online', 'ErrStatus', 'Point']);
        const configs = (this.device().config ?? []).filter(c => !EXCLUDED.has(c.id));
        return configs.map(c => ({
            id: c.id,
            name: c.name,
            value: this.device().data?.[c.id]?.value,
            isAlarm: this.device().data?.[c.id]?.isAlarm ?? false,
        }));
    });


    // 格式化會議室狀態 (01_1)
    formatRoomStatus(val: any): string {
        if (val === undefined || val === null || val === '' || val === 'null') return '-';
        const num = Number(val);
        switch (num) {
            case 0: return '閒置';
            case 1: return '已預約';
            case 2: return '使用中';
            default: return '-';
        }
    }

    // 獲取會議室狀態顏色類名
    getRoomStatusClass(val: any): string {
        if (val === undefined || val === null || val === '' || val === 'null') return '';
        const num = Number(val);
        switch (num) {
            case 0: return 'text-green-400';
            case 1: return 'text-yellow-400';
            case 2: return 'text-blue-400';
            default: return '';
        }
    }

    // 格式化捲簾狀態 (01_2)
    formatBlindStatus(val: any): string {
        if (val === undefined || val === null || val === '' || val === 'null') return '-';
        const num = Number(val);
        if (num === 0) return '全收(0%)';
        if (num === 100) return '全放(100%)';
        return `中間 (${num}%)`;
    }

    // 格式化冷氣模式
    formatMode(val: any): string {
        if (val === undefined || val === null) return '-';
        const num = Number(val);
        switch (num) {
            case 0: return '送風';
            case 2: return '冷氣';
            case 7: return '除濕';
            default: return '-';
        }
    }

    // 格式化冷氣錯誤狀態
    formatErrorStatus(val: any): string {
        if (val === undefined || val === null) return '-';
        const num = Number(val);
        return num === 0 ? '正常' : '異常';
    }

    // 格式化門禁狀態 (01_Door_Status)
    formatDoorStatus(val: any): string {
        if (val === undefined || val === null || val === '' || val === 'null') return '-';
        const num = Number(val);
        return num === 1 ? '開門' : '關門';
    }

    // 格式化門禁開門超時狀態 (01_Door_Open_Too_Long)
    formatDoorOpenTooLong(val: any): string {
        if (val === undefined || val === null || val === '' || val === 'null') return '-';
        const num = Number(val);
        return num === 1 ? '異常' : '正常';
    }

    // 格式化風機運轉模式 (01_RunMode)
    formatFanRunMode(val: any): string {
        if (val === undefined || val === null || val === '' || val === 'null') return '-';
        const num = Number(val);
        return num === 1 ? '普通換氣' : '全熱換氣';
    }

    // 格式化風機風速 (01_FanSpeed)
    formatFanSpeed(val: any): string {
        if (val === undefined || val === null || val === '' || val === 'null') return '-';
        const num = Number(val);
        switch (num) {
            case 1: return '弱';
            case 3: return '強';
            default: return `風速 ${num}`;
        }
    }

    // 格式化風機異常狀態 (02_ErrorStatus)
    formatFanErrorStatus(val: any): string {
        if (val === undefined || val === null || val === '' || val === 'null') return '-';
        const strVal = String(val);
        const options = EQUIPMENT_POINT_TAG_MAPPING['全熱交換器']?.['02_ErrorStatus']?.options;
        return options?.[strVal] || `異常 (代碼 ${val})`;
    }

    /**
     * AC 連線1 離線0
     * CCTV 連線1 離線0
     * PANEL 連線0 離線1
     * RB 連線0 離線1
     * HOST 連線1 離線0
     * AIR_QUALITY 連線0 離線1
     * POWER 連線0 離線1
     * DOOR 連線1 離線0
    **/
    // 格式化電源/連線狀態
    formatPower(val: any): string {
        if (val === undefined || val === null || val === '') return '-';
        const num = Number(val);

        switch (this.device().type) {
            case 'AC':
                // 0=關機, 1=開機
                return num === 1 ? '開機' : '關機';
            case 'POWER':
            case 'PANEL':
            case 'RB':
            case 'AIR_QUALITY':
            case 'FAN':
                // 0=連線中, 1=離線
                return num === 0 ? '連線中' : '離線';
            default:
                // 其他 (CCTV, HOST, DOOR)：1=連線中, 0=離線
                return num === 1 ? '連線中' : '離線';
        }
    }

    closePopup(event?: Event) {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        this.close.emit();
    }

    openDetail(event?: Event) {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        const dev = this.device();
        const equipmentType = dev.name.split('_')[0] || dev.type;

        // '/devices/detail/{code}/basic-info?deviceName=...&equipmentType=...'
        this.router.navigate(['/devices/detail', dev.deviceId, 'basic-info'], {
            queryParams: {
                deviceName: dev.name,
                equipmentType: equipmentType
            }
        });
    }
}
